import { Box, Typography } from '@mui/material';
import camelCase from 'lodash/camelCase';
import React from 'react';
import { useI18n } from '../../i18n/I18n';
import InviteInfoFeatureListItem from './components/InviteInfoFeatureListItem';
import { defaultFeatures } from './constants';
import { InviteInfoFeatureListItemType } from './types';

type InviteInfoPanelProps = {
    /**
     * Branding name to display
     */
    brandingName: string;
    /**
     * List of features to display
     */
    features?: Array<InviteInfoFeatureListItemType>;
    /**
     * Type of the invite
     */
    inviteType: string;
};

export default function InviteInfoPanel({
    brandingName = '',
    features = defaultFeatures,
    inviteType
}: InviteInfoPanelProps) {
    const t = useI18n();
    const inviteTypeKey = camelCase(inviteType);
    const titleText = t(`login.inviteType.${inviteTypeKey}.title`);
    const emphasizedText = t(`login.inviteType.${inviteTypeKey}.emphasizedText`);
    const splittedTitle = titleText.split('{emphasizedText}');

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                backgroundColor: 'inviteInfo.backgroundColor'
            }}
        >
            <Box sx={{ maxWidth: 600, width: '100%' }}>
                {/* Branding Name */}
                {brandingName && (
                    <Box
                        sx={{
                            position: 'relative',
                            width: 'fit-content',
                            margin: '0 auto',
                            padding: '16px 24px',
                            mb: '10px',
                            backgroundColor: 'inviteInfo.backgroundColor',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '8px',
                                padding: '2px',
                                background: 'linear-gradient(90deg, #E0360F 0%, #C2B920 50.96%, #19A6C0 100%)',
                                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                maskComposite: 'exclude'
                            }
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Sansita',
                                fontWeight: 400,
                                fontSize: '44px',
                                textAlign: 'center'
                            }}
                        >
                            {brandingName}
                        </Typography>
                    </Box>
                )}
                <Typography
                    fontSize={'24px'}
                    component="div"
                    sx={{
                        fontWeight: 400,
                        mb: 2,
                        textAlign: 'center'
                    }}
                >
                    {splittedTitle[0]}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 700,
                            fontStyle: 'italic'
                        }}
                    >
                        {emphasizedText}
                    </Box>
                    {splittedTitle[1]}
                </Typography>
                {/* Features List */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {features.map((feature, index) => (
                        <InviteInfoFeatureListItem
                            key={index}
                            iconName={feature.iconName}
                            title={feature.title}
                            description={feature.description}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
